import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlignLeft, UploadCloud, Loader2, FileText, FileAudio, AlertTriangle, Link2, Users, ScrollText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

const ACCEPT = ".pdf,.mp3,.wav,.m4a,.mp4";
const ACCEPT_SCRIPT = ".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp";

export default function AddContent() {
  const [tab, setTab] = useState("link");
  const [titolo, setTitolo] = useState("");
  const [testo, setTesto] = useState("");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [profile, setProfile] = useState(null);
  const [maxVideos, setMaxVideos] = useState(10);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duplicate, setDuplicate] = useState(null);
  const inputRef = useRef();
  const navigate = useNavigate();

  const words = testo.trim() ? testo.trim().split(/\s+/).length : 0;
  const urlValid = /^https?:\/\/\S+\.\S+$/.test(url.trim());

  useEffect(() => {
    if (!urlValid) { setProfile(null); return; }
    const t = setTimeout(() => {
      api.get("/contents/link/inspect", { params: { url: url.trim() } }).then((r) => setProfile(r.data)).catch(() => setProfile(null));
    }, 400);
    return () => clearTimeout(t);
  }, [url, urlValid]);

  const handleError = (e) => {
    if (e.response?.status === 409) {
      setDuplicate(e.response.data.detail);
      toast.warning("Contenuto già presente");
    } else toast.error(errorMessage(e));
  };

  const submit = async (e) => {
    e.preventDefault();
    setDuplicate(null);
    setLoading(true);
    try {
      let res;
      if (tab === "testo") {
        res = await api.post("/contents/text", { titolo: titolo || null, testo });
      } else if (tab === "link" && profile?.is_profile) {
        const { data } = await api.post("/contents/profile", { url: url.trim(), max_videos: maxVideos });
        toast.success(`Profilo ${data.profile_title}: ${data.in_coda} nuovi video in coda${data.duplicati ? `, ${data.duplicati} già analizzati saltati` : ""}${data.esauriti ? " · non ci sono altri video nuovi" : ""}`);
        if (data.nota) toast.info(data.nota, { duration: 8000 });
        navigate("/contenuti");
        return;
      } else if (tab === "link") {
        res = await api.post("/contents/link", { titolo: null, url });
      } else {
        const fd = new FormData();
        fd.append("file", file);
        if (titolo) fd.append("titolo", titolo);
        if (tab === "script") fd.append("is_script", "true");
        res = await api.post("/contents/file", fd, {
          onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
        });
      }
      toast.success("Contenuto caricato, elaborazione avviata");
      navigate(`/contenuti/${res.data.id}`);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

    const canSubmit = tab === "testo" ? testo.trim().length >= 20 : tab === "link" ? urlValid : !!file;
  const isUpload = tab === "file" || tab === "script";

  return (
    <div data-testid="add-content-page" className="max-w-3xl space-y-8">
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Ingestione</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Aggiungi contenuto</h1>
        <p className="mt-2 text-sm text-muted-foreground">Il contenuto verrà trascritto, analizzato e trasformato in conoscenza ricercabile.</p>
      </div>

      <form onSubmit={submit} className="fade-up surface space-y-6 p-6 sm:p-8">
        <div data-testid="add-content-tabs" className="inline-flex rounded-full bg-secondary p-1">
          {[
            { id: "link", label: "Link social", icon: Link2 },
            { id: "testo", label: "Incolla testo", icon: AlignLeft },
            { id: "file", label: "Carica file", icon: UploadCloud },
            { id: "script", label: "Carica script", icon: ScrollText },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                tab === id ? "bg-primary text-white shadow" : "text-muted-foreground hover:text-white"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab !== "link" && (
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Titolo (opzionale)</label>
          <input
            data-testid="title-input"
            value={titolo}
            onChange={(e) => setTitolo(e.target.value)}
            placeholder="Es. Webinar gestione obiezioni prezzo"
            className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
          />
        </div>
        )}

        {tab === "testo" ? (
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Testo</label>
              <span data-testid="word-count" className="text-xs font-mono text-muted-foreground">{words} parole</span>
            </div>
            <textarea
              data-testid="text-input"
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              rows={12}
              placeholder="Incolla qui appunti, script di vendita, trascrizioni…"
              className="mt-2 w-full resize-y rounded-lg border border-input bg-background px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
            />
          </div>
        ) : tab === "link" ? (
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Link (Instagram, TikTok, YouTube, Facebook, X, Vimeo…)</label>
            <div className="relative mt-2">
              <Link2 className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.tiktok.com/@utente/video/…"
                className="w-full rounded-lg border border-input bg-background py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-primary"
              />
            </div>
            {profile?.is_profile && (
              <div data-testid="profile-detected" className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm">
                <Users className="h-4 w-4 text-primary" />
                <span className="flex-1">Profilo <b>{profile.platform}</b> rilevato{profile.gia_scansionati > 0 && <span data-testid="profile-already" className="text-muted-foreground"> · {profile.gia_scansionati} video già analizzati, verranno saltati</span>}: prendo i prossimi</span>
                <select data-testid="profile-max-select" value={maxVideos} onChange={(e) => setMaxVideos(Number(e.target.value))} className="rounded-md border border-input bg-background px-2 py-1 text-sm outline-none">
                  {[5, 10, 20, 30, 50].map((n) => <option key={n} value={n}>{n} nuovi video</option>)}
                </select>
              </div>
            )}
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Incolla il link di un <strong className="text-foreground">singolo post/video</strong> oppure di un <strong className="text-foreground">profilo</strong>: in quel caso scansioniamo gli ultimi video e teniamo solo quelli sulla vendita (gli altri vengono saltati). Il titolo viene preso automaticamente dal post. Se la piattaforma
              blocca il download (es. post privati o Instagram con login richiesto), il contenuto andrà in ERRORE: in quel caso scarica il
              video e caricalo come file.
            </p>
          </div>
        ) : (
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{tab === "script" ? "Script (PDF, Word, TXT, foto · max 100 MB)" : "File (PDF, MP3, WAV, M4A, MP4 · max 100 MB)"}</label>
            {tab === "script" && (
              <p data-testid="script-hint" className="mt-1 text-xs leading-relaxed text-[#FCD990]">Le frasi dello script verranno trattate come <strong>frasi da dire</strong> e alimenteranno direttamente il suggeritore live. Puoi caricare anche una <strong>foto</strong> dello script: il testo viene letto con l'AI.</p>
            )}
            <input ref={inputRef} data-testid="file-input" type="file" accept={tab === "script" ? ACCEPT_SCRIPT : ACCEPT} className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div
              data-testid="file-dropzone"
              onClick={() => inputRef.current.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files?.[0] || null); }}
              className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-background p-10 text-center transition-colors hover:border-primary/60"
            >
              {file ? (
                <>
                  {/\.(png|jpe?g|webp)$/i.test(file.name) ? <ImageIcon className="h-8 w-8 text-primary" /> : /\.(mp3|wav|m4a|mp4)$/i.test(file.name) ? <FileAudio className="h-8 w-8 text-primary" /> : <FileText className="h-8 w-8 text-primary" />}
                  <p data-testid="selected-file-name" className="text-sm font-semibold">{file.name}</p>
                  <p className="text-xs font-mono text-muted-foreground">{(file.size / 1048576).toFixed(1)} MB</p>
                </>
              ) : (
                <>
                  <UploadCloud className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm">Trascina qui un file o <span className="text-primary underline">selezionalo</span></p>
                  <p className="text-xs text-muted-foreground">{tab === "script" ? "PDF, Word, TXT o foto dello script" : "Audio e video vengono trascritti con Deepgram Nova-3"}</p>
                </>
              )}
            </div>
          </div>
        )}

        {duplicate && (
          <div data-testid="duplicate-warning" className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div>
              <p className="font-semibold text-amber-300">Contenuto già presente</p>
              <p className="text-muted-foreground">
                Questo contenuto è identico a{" "}
                <Link data-testid="duplicate-link" to={`/contenuti/${duplicate.content_id}`} className="text-white underline">
                  {duplicate.titolo}
                </Link>
                . Non è stato rielaborato.
              </p>
            </div>
          </div>
        )}

        {loading && isUpload && progress > 0 && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}

        <button
          data-testid="submit-content-btn"
          type="submit"
          disabled={!canSubmit || loading}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1D4ED8] disabled:opacity-50 disabled:hover:translate-y-0"
        >
          {loading ? <Loader2 className="h-4 w-4 spin-slow" /> : <UploadCloud className="h-4 w-4" />}
          {loading ? (tab === "link" && profile?.is_profile ? "Scansione profilo…" : "Caricamento…") : tab === "link" && profile?.is_profile ? `Scansiona profilo (${maxVideos} video)` : "Avvia elaborazione"}
        </button>
      </form>
    </div>
  );
}
